import mixpanel from "mixpanel-browser";
import { track } from "../lib/posthog";
import { useState } from "react";
import { api } from "../api/client";
import { StatementOfOperationsOutput } from "../types/sop";
import { generateHtmlForPdf } from "./usePdfGenerate";

type DownloadErrorHandler = (message: string) => void;
type ApiErrorPayload = { message?: string; error?: string };
type ApiErrorResponse = { status?: number; data?: ApiErrorPayload };
type ApiLikeError = { message?: string; response?: ApiErrorResponse };

const getDownloadErrorMessage = (fallbackMessage: string, error: unknown) => {
  if (!error || typeof error !== "object") {
    return fallbackMessage;
  }

  const apiLikeError = error as ApiLikeError;
  const statusCode = apiLikeError.response?.status;
  const apiMessage =
    apiLikeError.response?.data?.message ??
    apiLikeError.response?.data?.error ??
    apiLikeError.message;

  if (apiMessage && statusCode) {
    return `${fallbackMessage} (HTTP ${statusCode}: ${apiMessage})`;
  }

  if (apiMessage) {
    return `${fallbackMessage} (${apiMessage})`;
  }

  return fallbackMessage;
};

const useMarkdownExport = (
  analyzedOutput: StatementOfOperationsOutput | null,
  onError?: DownloadErrorHandler,
) => {
  const [exportingMarkdown, setExportingMarkdown] = useState(false);
  const [sopMarkdownBlobUrl, setSopMarkdownBlobUrl] = useState<string | null>(
    null,
  );
  const [prevOutput, setPrevOutput] =
    useState<StatementOfOperationsOutput | null>(null);

  const triggerMarkdownDownload = (url: string, sopTitle: string) => {
    const anchorElement = document.createElement("a");
    anchorElement.href = url;
    anchorElement.download = `${sopTitle}.md`;
    document.body.appendChild(anchorElement);
    anchorElement.click();
    document.body.removeChild(anchorElement);
  };

  const downloadMarkdown = async () => {
    setExportingMarkdown(true);

    const sopTitle = analyzedOutput?.sopTitle?.trim() || "SOP Document";

    if (sopMarkdownBlobUrl && prevOutput === analyzedOutput) {
      triggerMarkdownDownload(sopMarkdownBlobUrl, sopTitle);
      setExportingMarkdown(false);
      return;
    }

    setPrevOutput(analyzedOutput);

    try {
      const htmlContent = await generateHtmlForPdf(analyzedOutput);
      const body: {
        html: string;
        sopId: string;
        sopTitle: string;
        title: string;
        version_id?: string;
      } = {
        html: htmlContent,
        sopId: analyzedOutput?.sopId || "",
        sopTitle,
        title: sopTitle,
      };
      if (analyzedOutput?.versionId) body.version_id = analyzedOutput.versionId;
      const response = await api.post("/generate-markdown", body, {
        responseType: "blob",
      });

      const markdownBlob = new Blob([response.data], {
        type: "text/markdown;charset=utf-8",
      });

      if (sopMarkdownBlobUrl) {
        URL.revokeObjectURL(sopMarkdownBlobUrl);
      }

      const markdownBlobUrl = URL.createObjectURL(markdownBlob);
      setSopMarkdownBlobUrl(markdownBlobUrl);
      triggerMarkdownDownload(markdownBlobUrl, sopTitle);
      try {
        mixpanel.track("Downloaded SOP Markdown");
        track("sop_downloaded", { format: "markdown" });
      } catch {
        // silent catch -- do nothing
      }
    } catch (error) {
      console.error("Markdown generation failed:", error);
      onError?.(
        getDownloadErrorMessage(
          "Failed to download Markdown. Please try again.",
          error,
        ),
      );
    } finally {
      setExportingMarkdown(false);
    }
  };

  return { exportingMarkdown, downloadMarkdown };
};

export default useMarkdownExport;
